import React from 'react'

import {
  DcBomCard,
  DcFlexBox,
  DcText,
  PixelSpinner,
  Section,
  SectionContent,
  colors,
  type BomCardMetric,
} from 'pixel-base'
import type { AjaxResponse } from 'rxjs/ajax'
import {
  getPlannerBom,
  type DemandForecastItem,
  type PlannerBomItem,
  type PlannerBomResponse,
} from 'utils/api-routes-planning'
import { BufferStockIcon } from 'utils/icons/buffer-stock-icon'
import { CalendarIcon } from 'utils/icons/calendar-icon'
import { GrowthFactorIcon } from 'utils/icons/growth-factor-icon'
import { LeadTimeIcon } from 'utils/icons/lead-time-icon'
import { MoqIcon } from 'utils/icons/moq-icon'
import { SalesQuantityIcon } from 'utils/icons/sales-quantity-icon'
import { TruckIcon } from 'utils/icons/truck-icon'
import { WarehouseIcon } from 'utils/icons/warehouse-icon'

interface BomDetailProps {
  header: DemandForecastItem
}

const BomDetail: React.FC<BomDetailProps> = ({ header }) => {
  const [items, setItems] = React.useState<PlannerBomItem[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!header?.product_id) return

    setIsLoading(true)
    setError(null)

    // Use the required_stock from the API if available, otherwise fallback to calculation
    const totalStock = header.warehouse_stock?.total ?? 0
    const expectedSales = header.expected_sales ?? 0
    const requiredStock = Math.round(
      header.required_stock !== undefined
        ? header.required_stock
        : Math.max(0, expectedSales - totalStock),
    )

    // Use product_id and pass required_stock as query parameter
    const subscription = getPlannerBom(
      header.product_id,
      requiredStock,
    ).subscribe({
      next: (res: AjaxResponse<PlannerBomResponse>) => {
        if (res.response?.error) {
          setError(res.response.error)
          setItems([])
        } else {
          setItems(res.response?.data || [])
        }
        setIsLoading(false)
      },
      error: () => {
        setError('Failed to load BOM data')
        setItems([])
        setIsLoading(false)
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [
    header?.product_id,
    header?.expected_sales,
    header?.warehouse_stock?.total,
  ])

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Header Section */}

      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '10px',
          padding: '16px',
          background: '#ffffff',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
          marginBottom: '16px',
        }}
      >
        {/* Product Info Section */}
        <DcFlexBox
          alignItems="center"
          gap="16px"
          height="auto"
          style={{
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          {header.product_image && (
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '8px',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
              }}
            >
              <img
                src={header.product_image}
                alt={header.product_name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  const parent = target.parentElement
                  if (parent) {
                    parent.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; font-size: 14px; color: #6b7280; text-align: center; padding: 4px; font-weight: 600;">${header.product_name
                      .substring(0, 2)
                      .toUpperCase()}</div>`
                  }
                }}
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DcText
              style={{
                fontWeight: 700,
                fontSize: '18px',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#111827',
              }}
            >
              {header.product_name}
            </DcText>
            <DcText
              style={{
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: '0px',
              }}
            >
              SKU:{' '}
              <span style={{ fontWeight: 600, color: '#374151' }}>
                {header.product_sku}
              </span>
            </DcText>
          </div>
        </DcFlexBox>

        {/* Metrics Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          {/* Avg Sales (6M) */}
          <DcFlexBox alignItems="center" gap="10px" height="auto">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: colors.primaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SalesQuantityIcon color={colors.primary} size={20} />
            </div>
            <div>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  marginBottom: '2px',
                }}
              >
                Avg Sales (6M)
              </DcText>
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: colors.primary,
                }}
              >
                {Math.round(header.avg_sales_6m ?? 0)}
              </DcText>
            </div>
          </DcFlexBox>

          {/* Growth Factor */}
          <DcFlexBox alignItems="center" gap="10px" height="auto">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(124, 58, 237, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <GrowthFactorIcon color="#7c3aed" size={20} />
            </div>
            <div>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  marginBottom: '2px',
                }}
              >
                Growth Factor
              </DcText>
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#7c3aed',
                }}
              >
                {header.growth_factor ?? 0}
              </DcText>
            </div>
          </DcFlexBox>

          {/* Expected Sales */}
          <DcFlexBox alignItems="center" gap="10px" height="auto">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(14, 165, 233, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarIcon color="#0ea5e9" size={20} />
            </div>
            <div>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  marginBottom: '2px',
                }}
              >
                Expected Sales
              </DcText>
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#0ea5e9',
                }}
              >
                {Math.round(header.expected_sales ?? 0)}
              </DcText>
            </div>
          </DcFlexBox>

          {/* In Stock */}
          <DcFlexBox alignItems="center" gap="10px" height="auto">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(22, 163, 74, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarehouseIcon color="#16a34a" size={20} />
            </div>
            <div>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  marginBottom: '2px',
                }}
              >
                In Stock
              </DcText>
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#16a34a',
                }}
              >
                {header.warehouse_stock?.total ?? 0}
              </DcText>
            </div>
          </DcFlexBox>

          {/* In Transit */}
          <DcFlexBox alignItems="center" gap="10px" height="auto">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TruckIcon color="#6366f1" size={20} />
            </div>
            <div>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  marginBottom: '2px',
                }}
              >
                In Transit
              </DcText>
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#6366f1',
                }}
              >
                {header.in_transit_stock ?? 0}
              </DcText>
            </div>
          </DcFlexBox>

          {/* Required Stock */}
          <DcFlexBox alignItems="center" gap="10px" height="auto">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(245, 158, 11, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BufferStockIcon color="#f59e0b" size={20} />
            </div>
            <div>
              <DcText
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  marginBottom: '2px',
                }}
              >
                Required Stock
              </DcText>
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#f59e0b',
                }}
              >
                {Math.round(header.required_stock ?? 0)}
              </DcText>
            </div>
          </DcFlexBox>
        </div>

        {/* Warehouse Breakdown for Header */}
        {header.warehouse_stock?.warehouses &&
          header.warehouse_stock.warehouses.length > 0 && (
            <div
              style={{
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
              }}
            >
              <DcText
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px',
                }}
              >
                Warehouse Stock Breakdown:
              </DcText>
              <DcFlexBox
                flexDirection="row"
                flexWrap="wrap"
                gap="10px"
                height="auto"
              >
                {header.warehouse_stock.warehouses.map((warehouse, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '6px 12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #f1f5f9',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                      minWidth: 'fit-content',
                    }}
                  >
                    <DcText
                      style={{
                        fontSize: '11px',
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {warehouse.warehouse_name}
                    </DcText>
                    <div
                      style={{
                        height: '12px',
                        width: '1px',
                        backgroundColor: '#e2e8f0',
                      }}
                    />
                    <DcText
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#0f172a',
                      }}
                    >
                      {warehouse.stock.toLocaleString()}
                    </DcText>
                  </div>
                ))}
              </DcFlexBox>
            </div>
          )}
      </div>

      {/* Cards Section */}
      <Section style={{ padding: '0px' }}>
        <SectionContent
          style={{
            overflow: 'auto',
            position: 'relative',
            height: 'calc(100vh - 330px)',
          }}
        >
          {isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px',
              }}
            >
              <PixelSpinner size="20px" isShow={isLoading} />
            </div>
          )}

          {!isLoading && error && (
            <DcText
              style={{ fontSize: '13px', color: '#ef4444', padding: '20px' }}
            >
              {error}
            </DcText>
          )}

          {!isLoading && !error && items.length === 0 && (
            <DcText
              style={{ fontSize: '13px', color: '#6b7280', padding: '20px' }}
            >
              No BOM data found for this product.
            </DcText>
          )}

          {!isLoading && !error && items.length > 0 && (
            <DcFlexBox
              width="100%"
              height="auto"
              flexDirection="column"
              gap="8px"
              flexWrap="nowrap"
            >
              {items.map((item) => {
                const metrics: BomCardMetric[] = [
                  {
                    label: 'Qty/Unit',
                    value:
                      typeof item.quantity_required_per_unit === 'number'
                        ? item.quantity_required_per_unit.toFixed(2)
                        : '--',
                    icon: BufferStockIcon,
                    iconColor: '#6366f1',
                    badgeBg: 'rgba(99, 102, 241, 0.05)',
                    badgeBorder: '1px solid rgba(99, 102, 241, 0.1)',
                    valueColor: '#6366f1',
                  },
                  {
                    label: 'Required',
                    value:
                      typeof item.required_bom_qty === 'number'
                        ? item.required_bom_qty.toFixed(1)
                        : '--',
                    icon: SalesQuantityIcon,
                    iconColor: colors.primary,
                    badgeBg: 'rgba(59, 130, 246, 0.05)',
                    badgeBorder: '1px solid rgba(59, 130, 246, 0.1)',
                    valueColor: colors.primary,
                  },
                  {
                    label: 'In Stock',
                    value: item.bom_in_stock ?? 0,
                    icon: WarehouseIcon,
                    iconColor: '#22c55e',
                    badgeBg: 'rgba(34, 197, 94, 0.05)',
                    badgeBorder: '1px solid rgba(34, 197, 94, 0.1)',
                    valueColor: '#22c55e',
                  },
                  {
                    label: 'In Transit',
                    value: item.in_transit_stock ?? 0,
                    icon: TruckIcon,
                    iconColor: '#6366f1',
                    badgeBg: 'rgba(99, 102, 241, 0.05)',
                    badgeBorder: '1px solid rgba(99, 102, 241, 0.1)',
                    valueColor: '#6366f1',
                  },
                  {
                    label: 'Lead Time',
                    value: `${item.lead_time ?? 0}d`,
                    icon: LeadTimeIcon,
                    iconColor: '#0ea5e9',
                    badgeBg: 'rgba(14, 165, 233, 0.05)',
                    badgeBorder: '1px solid rgba(14, 165, 233, 0.1)',
                    valueColor: '#0ea5e9',
                  },
                  {
                    label: 'MOQ',
                    value: item.moq ?? 0,
                    icon: MoqIcon,
                    iconColor: colors.secondary,
                    badgeBg: 'rgba(168, 85, 247, 0.05)',
                    badgeBorder: '1px solid rgba(168, 85, 247, 0.1)',
                    valueColor: colors.secondary,
                  },
                ]

                return (
                  <DcBomCard
                    key={item.component_product_id}
                    componentName={item.component_name}
                    componentSku={item.component_sku}
                    metrics={metrics}
                    warehouses={item.warehouse}
                  />
                )
              })}
            </DcFlexBox>
          )}
        </SectionContent>
      </Section>
    </div>
  )
}

export default BomDetail
